import { useEffect, useRef, useState } from 'react';
import RFB from '@novnc/novnc';
import { getVncPassword, vncWsUrl } from '@/api/real-browsers';
import { cn } from '@/lib/cn';

interface Props {
  instanceId: string;
  /**
   * View-only mode disables mouse/keyboard input — useful when the
   * dashboard is shown on a TV / read-only console.
   */
  viewOnly?: boolean;
}

/**
 * Embeds the noVNC client connected through the otr-core WebSocket
 * proxy at /v1/real-browsers/instances/:id/vnc.
 *
 * The library handles the entire VNC protocol + canvas rendering +
 * mouse/keyboard/clipboard events. We only own the connection
 * lifecycle: open on mount, close on unmount or instanceId change.
 *
 * Connection state is shown above the canvas so the operator knows
 * when the upstream multi-chrome:6080 socket is reachable.
 */
export default function VncViewer({ instanceId, viewOnly = false }: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<RFB | null>(null);
  const [state, setState] = useState<'connecting' | 'connected' | 'disconnected' | 'failed'>('connecting');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!frameRef.current) return;
    setState('connecting');
    setError(null);

    const url = vncWsUrl(instanceId);
    let rfb: RFB;
    try {
      // x11vnc requires a password; it is fetched (behind the same API
      // auth) when the server asks for it — see onCredentialsRequired.
      rfb = new RFB(frameRef.current, url);
    } catch (e: any) {
      setState('failed');
      setError(e?.message ?? 'rfb_init_failed');
      return;
    }

    rfb.viewOnly = viewOnly;
    rfb.scaleViewport = true;
    rfb.resizeSession = false;

    const onConnect = () => setState('connected');
    const onDisconnect = (e: any) => {
      setState('disconnected');
      if (e?.detail?.reason) setError(String(e.detail.reason));
    };
    const onSecurityFailure = (e: any) => {
      setState('failed');
      setError(e?.detail?.reason ?? 'security_failure');
    };

    const onCredentialsRequired = () => {
      getVncPassword()
        .then((password) => rfb.sendCredentials({ password }))
        .catch((e: any) => {
          setState('failed');
          setError(e?.message ?? 'vnc_credentials_failed');
        });
    };

    rfb.addEventListener('connect', onConnect);
    rfb.addEventListener('credentialsrequired', onCredentialsRequired);
    rfb.addEventListener('disconnect', onDisconnect);
    rfb.addEventListener('securityfailure', onSecurityFailure);
    rfbRef.current = rfb;

    return () => {
      rfb.removeEventListener('connect', onConnect);
      rfb.removeEventListener('credentialsrequired', onCredentialsRequired);
      rfb.removeEventListener('disconnect', onDisconnect);
      rfb.removeEventListener('securityfailure', onSecurityFailure);
      try { rfb.disconnect(); } catch { /* ignore */ }
      rfbRef.current = null;
    };
  }, [instanceId, viewOnly]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <span className={cn('dot',
          state === 'connected'    && 'bg-emerald-500',
          state === 'connecting'   && 'bg-yellow-500 animate-pulse',
          state === 'disconnected' && 'bg-slate-500',
          state === 'failed'       && 'bg-red-500',
        )} />
        <span className="text-slate-400 font-mono">{state}</span>
        {error && <span className="text-red-400 truncate">— {error}</span>}
      </div>

      <div ref={frameRef} className="vnc-frame" />

      {state === 'failed' && (
        <p className="text-xs text-slate-500">
          Couldn't reach noVNC. Confirm the multi-chrome container is running
          and `vnc_endpoint` on the pool is correct.
        </p>
      )}
    </div>
  );
}
